/**
 * StateContextGate
 * ─────────────────────────────────────────────────────────────
 * On first visit, resolves the visitor's state from Cloudflare's
 * `cf-region` header (surfaced via a lightweight `/api/geo/state`
 * endpoint, or via a browser fallback that reads `navigator.language`
 * and IP-geolocation ancillaries). If the visitor is NOT in SA and
 * has never seen this toast before, we show a friendly opt-in banner
 * telling them BuildPilot is currently SA-only and offering a waitlist.
 *
 * Behaviour rules:
 *   - Never blocks content. Dismissable. Sticky bottom-right toast.
 *   - Never fires for SA visitors.
 *   - Fires at most once per browser (localStorage flag).
 *   - Fires only after the visitor has spent 8+ seconds on the page
 *     (so we don't interrupt the first paint).
 *   - Waitlist opt-in goes to /waitlist?state=<code>.
 *
 * Multi-state ready: when we ship VIC, add 'VIC' to the LIVE_STATES
 * set. Visitors from a LIVE state get no toast; visitors from a
 * non-live state see the toast with their state pre-selected.
 */
import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { isLiveState } from '../../lib/userState';

const STORAGE_KEY = 'bp:stateGate:dismissed';
const STATE_NAMES = {
  VIC: 'Victoria', NSW: 'New South Wales', QLD: 'Queensland',
  WA: 'Western Australia', TAS: 'Tasmania', NT: 'Northern Territory',
  ACT: 'Australian Capital Territory', SA: 'South Australia',
};

const StateContextGate = () => {
  const [detectedState, setDetectedState] = useState(null);
  const [visible, setVisible] = useState(false);

  const dismiss = useCallback(() => {
    try { window.localStorage.setItem(STORAGE_KEY, '1'); } catch {}
    setVisible(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let showTimer;

    // Skip if already dismissed.
    try {
      if (window.localStorage.getItem(STORAGE_KEY)) return;
    } catch {}

    // Ask the backend to resolve state from Cloudflare's cf-region
    // header. If the endpoint isn't wired yet, we quietly no-op.
    (async () => {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/geo/state`;
        const res = await fetch(url, { credentials: 'omit' });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const state = (data.state || '').toUpperCase();
        if (!state || isLiveState(state)) return; // live state (SA/VIC) - no toast.
        setDetectedState(state);
        // Delay show so first paint isn't interrupted.
        showTimer = window.setTimeout(() => {
          if (!cancelled) setVisible(true);
        }, 8000);
      } catch {
        // Network error, ad-block, etc. Silently no-op.
      }
    })();

    return () => {
      cancelled = true;
      if (showTimer) window.clearTimeout(showTimer);
    };
  }, []);

  if (!visible || !detectedState) return null;

  const stateName = STATE_NAMES[detectedState] || detectedState;

  return (
    <div
      role="dialog"
      aria-label={`BuildPilot state notice for ${stateName}`}
      className="fixed bottom-4 right-4 z-[9998] max-w-[380px] bg-white rounded-2xl shadow-2xl border border-[#e8e8ed] overflow-hidden"
      data-testid="state-context-gate"
    >
      <div className="bg-[#eef4ff] px-4 py-2.5 flex items-center justify-between border-b border-[#dae6fb]">
        <span className="text-[13px] font-semibold text-[#1d8efe]" data-testid="state-context-gate-detected">
          <span aria-hidden="true">📍</span> Looks like you're in {stateName}
        </span>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Close"
          className="text-[#86868b] hover:text-[#1d1d1f]"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="px-4 py-3 text-[13px] text-[#424245] leading-relaxed">
        BuildPilot is currently live in <strong>South Australia and Victoria</strong>.
        Prices, rules and builders on the site apply to those states.
        {' '}Want to be the first to know when we launch in {stateName}?
      </div>
      <div className="px-4 pb-4 flex gap-2">
        <Link
          to={`/waitlist?state=${detectedState}`}
          onClick={dismiss}
          className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#1d8efe] hover:bg-[#1670cc] text-white px-3 py-2 rounded-full text-[13px] font-semibold transition-colors"
          data-testid="state-context-gate-notify"
        >
          Notify me for {detectedState}
        </Link>
        <button
          type="button"
          onClick={dismiss}
          className="inline-flex items-center justify-center px-3 py-2 rounded-full text-[13px] font-medium text-[#424245] hover:text-[#1d1d1f] border border-[#e8e8ed]"
          data-testid="state-context-gate-dismiss"
        >
          Continue anyway
        </button>
      </div>
    </div>
  );
};

export default StateContextGate;
